import React from 'react';
import { Ticket, TICKET_STATUS_CONFIG, TICKET_CATEGORY_CONFIG, TicketStatus, TicketPriority, TICKET_PRIORITY_CONFIG } from './types';
import { StatusBadge, UserAvatar, TicketRowSkeleton } from './TicketShared';
import { MoreVertical, Trash2, ExternalLink, UserCheck, Calendar } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';

interface TicketListViewProps {
  tickets: Ticket[];
  loading: boolean;
  onOpenTicket: (ticket: Ticket) => void;
  onChangeStatus: (ticketId: string, status: TicketStatus) => void;
  onDeleteTicket: (ticketId: string) => void;
  isTeamMember?: boolean;
}

function formatOrdinalDate(dateStr?: string | null) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const day = d.getDate();
  const suffix = ["th", "st", "nd", "rd"][day % 10 > 3 ? 0 : (day % 100 - day % 10 != 10) ? day % 10 : 0];
  const month = d.toLocaleDateString('en-US', { month: 'long' });
  const year = d.getFullYear();
  return `${day}${suffix} ${month}, ${year}`;
}

export const TicketListView: React.FC<TicketListViewProps> = ({
  tickets,
  loading,
  onOpenTicket,
  onChangeStatus,
  onDeleteTicket,
  isTeamMember = false,
}) => {
  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-white dark:bg-card overflow-hidden">
        <table className="w-full">
          <thead>
            <TicketTableHeader />
          </thead>
          <tbody>
            {[...Array(5)].map((_, i) => <TicketRowSkeleton key={i} />)}
          </tbody>
        </table>
      </div>
    );
  }

  if (tickets.length === 0) {
    return null; // empty state handled by parent
  }

  return (
    <div className="rounded-xl border border-border bg-white dark:bg-card overflow-hidden">
      <table className="w-full">
        <thead>
          <TicketTableHeader />
        </thead>
        <tbody>
          {tickets.map(ticket => (
            <TicketRow
              key={ticket.id}
              ticket={ticket}
              onOpen={() => onOpenTicket(ticket)}
              onChangeStatus={onChangeStatus}
              onDelete={onDeleteTicket}
              isTeamMember={isTeamMember}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const TicketTableHeader: React.FC = () => (
  <tr className="border-b border-border bg-muted/30">
    <th className="text-left px-4 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Ticket</th>
    <th className="text-left px-4 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden xl:table-cell">Category</th>
    <th className="text-left px-4 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden lg:table-cell">Client</th>
    <th className="text-left px-4 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden md:table-cell">Assigned</th>
    <th className="text-left px-4 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden md:table-cell">Status</th>
    <th className="text-right px-4 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
  </tr>
);

interface TicketRowProps {
  ticket: Ticket;
  onOpen: () => void;
  onChangeStatus: (ticketId: string, status: TicketStatus) => void;
  onDelete: (ticketId: string) => void;
  isTeamMember?: boolean;
}

const PRIORITY_TEXT_COLOR: Record<TicketPriority, string> = {
  urgent: 'text-red-500',
  high: 'text-orange-500',
  normal: 'text-blue-500',
  low: 'text-muted-foreground',
};

const TicketRow: React.FC<TicketRowProps> = ({ ticket, onOpen, onChangeStatus, onDelete, isTeamMember = false }) => {
  const isUrgent = ticket.priority === 'urgent';
  const isOpen = ticket.status === 'open';

  return (
    <tr
      className={`border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer ${
        isUrgent && isOpen ? 'border-l-2 border-l-red-500' : ''
      }`}
      onClick={onOpen}
    >
      {/* Subject + Number + Date + Priority */}
      <td className={`px-4 py-3.5 min-w-0 max-w-[350px] ${isUrgent && isOpen ? 'pl-[18px]' : ''}`}>
        <div className="flex-1 min-w-0">
          <h3 className="text-[15px] font-medium text-foreground mb-1.5 truncate">
            {ticket.subject}
          </h3>
          
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-muted-foreground">
            <span className="font-medium">#TID {ticket.ticket_number}</span>
            
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3 h-3 opacity-70" />
              <span>{formatOrdinalDate(ticket.created_at)}</span>
            </div>
            
            <div className="flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${TICKET_PRIORITY_CONFIG[ticket.priority]?.dotColor || 'bg-muted-foreground'}`} />
              <span className={PRIORITY_TEXT_COLOR[ticket.priority] || 'text-muted-foreground'}>
                {TICKET_PRIORITY_CONFIG[ticket.priority]?.label}
              </span>
            </div>
            
            {/* Mobile status badge */}
            <div className="mt-1 md:hidden ml-1">
              <StatusBadge status={ticket.status} size="sm" />
            </div>
          </div>
        </div>
      </td>

      {/* Category */}
      <td className="px-4 py-3.5 hidden xl:table-cell">
        <span className="text-sm text-muted-foreground">
          {TICKET_CATEGORY_CONFIG[ticket.category]?.label || ticket.category}
        </span>
      </td>

      {/* Client */}
      <td className="px-4 py-3.5 hidden lg:table-cell">
        {ticket.client ? (
          <div className="flex items-center gap-2">
            <UserAvatar name={ticket.client.name} avatar={ticket.client.avatar} size="sm" />
            <span className="text-sm text-foreground truncate max-w-[120px]">{ticket.client.name}</span>
          </div>
        ) : (
          <span className="text-muted-foreground/40 text-sm">—</span>
        )}
      </td>

      {/* Assignee */}
      <td className="px-4 py-3.5 hidden md:table-cell">
        {ticket.assignee ? (
          <div className="flex items-center gap-2">
            <UserAvatar name={ticket.assignee.name} avatar={ticket.assignee.avatar} size="sm" />
            <span className="text-sm text-foreground truncate max-w-[100px]">{ticket.assignee.name}</span>
          </div>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-500/10 px-2 py-0.5 rounded-full">
            <UserCheck className="w-3 h-3" /> Unassigned
          </span>
        )}
      </td>

      {/* Status */}
      <td className="px-4 py-3.5 hidden md:table-cell">
        <StatusBadge status={ticket.status} />
      </td>

      {/* Actions */}
      <td className="px-4 py-3.5 text-right" onClick={e => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
              <MoreVertical className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onClick={onOpen}>
              <ExternalLink className="w-4 h-4" /> Open Ticket
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {Object.entries(TICKET_STATUS_CONFIG)
              .filter(([val]) => {
                if (val === ticket.status) return false;
                if (!isTeamMember && (val === 'in_progress' || val === 'waiting_on_client')) {
                  return val === ticket.status; 
                }
                return true;
              })
              .map(([val, cfg]) => (
                <DropdownMenuItem
                  key={val}
                  onClick={() => onChangeStatus(ticket.id, val as TicketStatus)}
                >
                  <span className={`w-2 h-2 rounded-full ${cfg.dotColor}`} />
                  Mark as {cfg.label}
                </DropdownMenuItem>
              ))}
            {isTeamMember && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  destructive
                  onClick={() => onDelete(ticket.id)}
                >
                  <Trash2 className="w-4 h-4" /> Delete Ticket
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
};
